import { useEffect, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Pencil, Plus, Shield, Trash2, UserCog, UserX } from 'lucide-react'
import { createUserSchema, formatDateTime, formatNumber, PERMISSIONS } from '@najah/core'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, ConfirmDialog, Dialog, EmptyState, Input,
  Pagination, Select, Switch, Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { usersApi, type RoleRow, type UserRow } from '../lib/settingsApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'

function UserFormDialog({
  open, onClose, user, roles,
}: { open: boolean; onClose: () => void; user: UserRow | null; roles: RoleRow[] }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()

  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [roleId, setRoleId] = useState('')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (!open) return
    clear()
    setUsername(user?.username ?? '')
    setFullName(user?.fullName ?? '')
    setPhone(user?.phone ?? '')
    setPassword('')
    setRoleId(user ? (roles.find((r) => r.nameAr === user.roleNameAr)?.id ?? '') : (roles[0]?.id ?? ''))
    setIsActive(user?.isActive ?? true)
  }, [open, user, roles, clear])

  const mutation = useMutation({
    mutationFn: async () => {
      if (user) {
        // التعديل لا يمسّ كلمة المرور — لها مسار منفصل
        return usersApi.update(user.id, {
          username: username.trim(),
          fullName: fullName.trim(),
          phone: phone.trim(),
          roleId,
          isActive,
        })
      }
      const parsed = validate(createUserSchema, {
        username: username.trim(),
        fullName: fullName.trim(),
        phone: phone.trim(),
        password,
        roleId,
        isActive,
      })
      if (!parsed) throw new Error('VALIDATION')
      return usersApi.create(parsed)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success(
        user ? 'حُفظ المستخدم' : 'أُنشئ المستخدم',
        user ? undefined : 'سيُطلب منه تغيير كلمة المرور عند أول دخول',
      )
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر حفظ المستخدم')
    },
  })

  const selectedRole = roles.find((r) => r.id === roleId)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={user ? `تعديل: ${user.fullName}` : 'مستخدم جديد'}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>حفظ</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="الاسم الكامل" required autoFocus
            value={fullName} onChange={(e) => setFullName(e.target.value)} error={errors.fullName}
          />
          <Input
            label="اسم المستخدم" required ltr
            value={username} onChange={(e) => setUsername(e.target.value)} error={errors.username}
            hint="أحرف لاتينية وأرقام"
          />
          <Input
            label="الهاتف" ltr
            value={phone} onChange={(e) => setPhone(e.target.value)} error={errors.phone}
          />
          <Select
            label="الدور" required
            value={roleId} onChange={(e) => setRoleId(e.target.value)}
            options={roles.map((r) => ({ value: r.id, label: r.nameAr }))}
            error={errors.roleId}
          />
        </div>

        {!user && (
          <Input
            label="كلمة مرور مؤقّتة" required type="text"
            value={password} onChange={(e) => setPassword(e.target.value)} error={errors.password}
            hint="٨ أحرف على الأقل — يُجبَر على تغييرها عند أول دخول"
          />
        )}

        {selectedRole && (
          <div className="rounded-lg bg-bg-subtle p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-fg-muted">
              <Shield className="size-3.5" />
              صلاحيات «{selectedRole.nameAr}» — {formatNumber(selectedRole.permissions.length)}
            </p>
            <div className="flex flex-wrap gap-1">
              {selectedRole.permissions.slice(0, 10).map((p) => (
                <Badge key={p} tone="neutral" className="text-[10px]">{p}</Badge>
              ))}
              {selectedRole.permissions.length > 10 && (
                <Badge tone="neutral" className="text-[10px]">
                  +{formatNumber(selectedRole.permissions.length - 10)}
                </Badge>
              )}
            </div>
          </div>
        )}

        <Switch
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          label="حساب نشط"
          description="التعطيل يقطع وصوله فوراً ويُنهي جلساته"
        />
      </div>
    </Dialog>
  )
}

function ResetPasswordDialog({ user, onClose }: { user: UserRow | null; onClose: () => void }) {
  const toast = useToast()
  const { errors, formError, handleApiError } = useFormErrors()
  const [password, setPassword] = useState('')

  useEffect(() => { setPassword('') }, [user])

  const mutation = useMutation({
    mutationFn: () => usersApi.resetPassword(user!.id, password),
    onSuccess: () => {
      toast.success('أُعيد تعيين كلمة المرور', 'جلساته أُنهيت وسيُطلب منه تغييرها')
      onClose()
    },
    onError: (err) => handleApiError(err, 'تعذّرت إعادة التعيين'),
  })

  return (
    <Dialog
      open={!!user}
      onClose={onClose}
      size="sm"
      title="إعادة تعيين كلمة المرور"
      description={user?.fullName}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={password.length < 8}>
            إعادة التعيين
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}
        <Alert tone="warning">
          ستُنهى كل جلساته فوراً، وسيُطلب منه تغيير كلمة المرور عند أول دخول —
          فلن تبقى الكلمة التي تكتبها هنا معروفة لك.
        </Alert>
        <Input
          label="كلمة مرور مؤقّتة" required type="text" autoFocus
          value={password} onChange={(e) => setPassword(e.target.value)}
          error={errors.newPassword}
          hint="٨ أحرف على الأقل — سلّمها للموظّف"
        />
      </div>
    </Dialog>
  )
}

export function UsersPage() {
  const { user: me, can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.USER_MANAGE)

  const [page, setPage] = useState(1)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<UserRow | null>(null)
  const [resetting, setResetting] = useState<UserRow | null>(null)
  const [deleting, setDeleting] = useState<UserRow | null>(null)

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['users', page],
    queryFn: () => usersApi.list({ page, pageSize: 25 }),
    placeholderData: keepPreviousData,
    enabled: canManage,
  })
  const { data: roles } = useQuery({ queryKey: ['users', 'roles'], queryFn: usersApi.roles, enabled: canManage })

  const removeMutation = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success('حُذف المستخدم')
      setDeleting(null)
    },
    onError: (err) => {
      toast.error('تعذّر الحذف', err instanceof Error ? err.message : undefined)
      setDeleting(null)
    },
  })

  if (!canManage) {
    return <Alert tone="danger" title="غير مصرّح">إدارة المستخدمين تتطلّب صلاحية «إدارة المستخدمين».</Alert>
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">المستخدمون</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            {data ? `${formatNumber(data.total)} حساب` : 'جارٍ التحميل…'}
          </p>
        </div>
        <Button startIcon={<Plus className="size-4" />} onClick={() => { setEditing(null); setFormOpen(true) }}>
          مستخدم جديد
        </Button>
      </div>

      <Alert tone="info">
        لكل موظّف حساب باسمه ودوره — لا حساب مشترك. سجل التدقيق يربط كل عملية بمنفّذها،
        والحساب المشترك يُلغي هذه الفائدة تماماً.
      </Alert>

      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th>الموظّف</Th>
              <Th>اسم المستخدم</Th>
              <Th>الدور</Th>
              <Th>آخر دخول</Th>
              <Th align="center">الحالة</Th>
              <Th align="center">إجراءات</Th>
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={5} cols={6} />
          ) : (
            <Tbody>
              {data?.items.map((u) => {
                const isSelf = u.id === me?.id
                return (
                  <Tr key={u.id} className={cn(!u.isActive && 'opacity-55')}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-subtle text-xs font-semibold text-primary-subtle-fg">
                          {u.fullName.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('')}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-fg">{u.fullName}</p>
                          {isSelf && <span className="text-[11px] text-fg-subtle">أنت</span>}
                        </div>
                      </div>
                    </Td>
                    <Td className="font-mono text-xs text-fg-muted" dir="ltr">{u.username}</Td>
                    <Td><Badge tone="primary">{u.roleNameAr}</Badge></Td>
                    <Td className="whitespace-nowrap text-xs text-fg-muted">
                      {u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'لم يدخل بعد'}
                    </Td>
                    <Td align="center">
                      {u.isActive
                        ? <Badge tone="success" dot>نشط</Badge>
                        : <Badge tone="neutral" dot>معطّل</Badge>}
                    </Td>
                    <Td align="center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost" size="icon" title="تعديل"
                          onClick={() => { setEditing(u); setFormOpen(true) }}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="إعادة تعيين كلمة المرور" onClick={() => setResetting(u)}>
                          <KeyRound className="size-4" />
                        </Button>
                        {!isSelf && (
                          <Button
                            variant="ghost" size="icon" title="حذف"
                            className="text-danger hover:bg-danger-subtle"
                            onClick={() => setDeleting(u)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState icon={<UserX className="size-10" />} title="لا يوجد مستخدمون" />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page} totalPages={data.totalPages} total={data.total} pageSize={data.pageSize}
            onPageChange={setPage}
          />
        )}
      </TableWrapper>

      {/* الأدوار للاطّلاع — التعديل عليها غير متاح بعد */}
      <Card>
        <CardHeader
          title="الأدوار والصلاحيات"
          description="أدوار النظام جاهزة — تخصيص الأدوار يُضاف لاحقاً"
        />
        <CardBody className="space-y-3">
          {roles?.map((r) => (
            <div key={r.id} className="rounded-lg border border-border p-3">
              <div className="mb-2 flex items-center gap-2">
                <UserCog className="size-4 text-fg-subtle" />
                <span className="font-medium text-fg">{r.nameAr}</span>
                <Badge tone="neutral">{formatNumber(r.permissions.length)} صلاحية</Badge>
                {r.isSystem && <Badge tone="info" className="ms-auto">دور نظامي</Badge>}
              </div>
              <div className="flex flex-wrap gap-1">
                {r.permissions.map((p) => (
                  <Badge key={p} tone="neutral" className="text-[10px]">{p}</Badge>
                ))}
              </div>
            </div>
          ))}
        </CardBody>
      </Card>

      <UserFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null) }}
        user={editing}
        roles={roles ?? []}
      />
      <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && removeMutation.mutate(deleting.id)}
        loading={removeMutation.isPending}
        title="حذف المستخدم"
        confirmLabel="حذف"
        description={
          <>
            سيُحذف <span className="font-semibold text-fg">{deleting?.fullName}</span> وتُنهى جلساته فوراً.
            <br />
            عملياته السابقة تبقى في السجل منسوبة إليه.
          </>
        }
      />
    </div>
  )
}
